import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Coffee, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const CoffeeComparison = () => {
  const [visibleCoffees, setVisibleCoffees] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisibleCoffees(prev => {
        if (prev < 5) {
          return prev + 1;
        }
        return 5; // Keep all 5 visible
      });
    }, 800); // Show one coffee every 800ms

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative overflow-hidden py-16 md:py-24 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-zinc-900 dark:to-zinc-900">
      {/* Dark: tie into the zinc grid section above — cool blue lift at the fold (no near-black strip from page background). */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 z-0 h-28 bg-gradient-to-b from-blue-500/[0.07] via-blue-400/[0.02] to-transparent opacity-0 dark:opacity-100"
        aria-hidden
      />
      {/* Dark: hero-adjacent atmosphere — blue/indigo wash, replaces warm orange cast */}
      <div
        className="pointer-events-none absolute inset-0 z-0 opacity-0 dark:opacity-100 dark:bg-[radial-gradient(ellipse_100%_62%_at_50%_-6%,rgba(59,130,246,0.12),transparent_56%),radial-gradient(ellipse_70%_45%_at_100%_55%,rgba(99,102,241,0.07),transparent_52%)]"
        aria-hidden
      />
      <div className="container relative z-10 px-4 md:px-6 max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center space-y-12"
        >
          {/* Main Pricing Section - Side by Side Layout */}
          <div className="grid lg:grid-cols-2 gap-8 items-center max-w-6xl mx-auto">
            {/* Pricing Card - Left Side */}
            <div className="rounded-3xl border border-zinc-200 bg-white p-6 shadow-lg sm:p-8 md:p-12 dark:border-zinc-600/45 dark:bg-zinc-800 dark:shadow-[0_1px_0_0_rgba(255,255,255,0.05)_inset,0_14px_44px_-14px_rgba(0,0,0,0.5)]">
              <div className="space-y-6">
                <div className="text-center lg:text-left">
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900 dark:text-zinc-100 mb-4">
                    LaunchGuide Pro
                  </h2>
                  <div className="flex items-center justify-center lg:justify-start gap-3 sm:gap-4 mb-6">
                    <div className="text-5xl sm:text-6xl md:text-7xl font-bold gradient-text">
                      €29
                    </div>
                    <div className="text-base sm:text-lg text-zinc-500 dark:text-zinc-400">
                      per month
                    </div>
                  </div>
                  <p className="text-base sm:text-lg md:text-xl text-zinc-600 dark:text-zinc-300 mb-6">
                    Unlock every playbook, AI tool, and analysis workflow
                  </p>
                </div>

                {/* What's Included */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-zinc-700 dark:text-zinc-300">All playbooks and phases unlocked</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-zinc-700 dark:text-zinc-300">Research, content & launch tools</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-zinc-700 dark:text-zinc-300">Ongoing updates included</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-zinc-700 dark:text-zinc-300">Cancel anytime</span>
                  </div>
                </div>

                <Button 
                  asChild
                  size="lg"
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <Link to="/auth/register">Subscribe for €29/month</Link>
                </Button>
              </div>
            </div>

            {/* Coffee Comparison - Right Side */}
            <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-6 shadow-lg sm:p-8 dark:border-blue-500/18 dark:bg-gradient-to-br dark:from-zinc-800 dark:to-zinc-800 dark:shadow-[0_1px_0_0_rgba(255,255,255,0.04)_inset,0_10px_36px_-12px_rgba(0,0,0,0.45)]">
              <div className="text-center space-y-6">
                <h3 className="text-xl sm:text-2xl font-bold text-zinc-900 dark:text-zinc-100">
                  That's less than your weekly coffee budget
                </h3>
                
                <div className="space-y-4">
                  <p className="text-zinc-600 dark:text-zinc-400 text-lg">
                    €29 = 5 iced lattes ☕
                  </p>
                  
                  {/* Coffee Animation */}
                  <div className="flex justify-center items-center gap-3 h-20">
                    {[...Array(5)].map((_, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{
                          opacity: index < visibleCoffees ? 1 : 0,
                          scale: index < visibleCoffees ? 1 : 0
                        }}
                        transition={{
                          duration: 0.4,
                          type: "spring",
                          stiffness: 200
                        }}
                        className="flex flex-col items-center"
                      >
                        <Coffee className="h-10 w-10 text-amber-600" />
                        <span className="text-sm text-zinc-500 mt-1 font-medium">€5.80</span>
                      </motion.div>
                    ))}
                  </div>
                  
                  <div className="space-y-2">
                    <p className="text-zinc-600 dark:text-zinc-300 font-medium">
                      But instead of 5 coffees, you get:
                    </p>
                    <p className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                      A complete system to reach $10K MRR
                  </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="text-center">
            <p className="text-base sm:text-lg text-zinc-600 dark:text-zinc-300 mb-6">
              Join founders building successful products with proven strategies
            </p>
            <Button 
              asChild
              size="lg"
              variant="ghost"
              className="h-11 w-full rounded-md border border-blue-600/80 bg-transparent px-6 text-base font-semibold text-blue-600 shadow-none transition-all duration-300 hover:bg-blue-50 hover:text-blue-700 dark:border-blue-400/35 dark:text-blue-300 dark:hover:border-blue-400/55 dark:hover:bg-blue-500/[0.12] dark:hover:text-blue-100 sm:h-12 sm:w-auto sm:px-8 sm:text-lg"
            >
              <Link to="/auth/register">Start Your Journey Today</Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CoffeeComparison; 